import { filterType } from '../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
  ListFilterResponseProperties,
} from '../interface/filter.interface';

/**
 * Function to realign list filter response
 * @param filters
 */
function ConfigureListFilterResponse(filters: ListFilterResponseProperties[]) {
  const updatedFilters: CommonFilterProperties[] = filters.map((filter) => {
    let singleFilter;
    if (
      filter.subFilters.type === filterType.DATE_RANGE ||
      filter.subFilters.type === filterType.TIME_RANGE
    ) {
      singleFilter = {
        name: filter.label,
        key: filter.subFilters.type,
        value: '',
        key1: filter.value1,
        value1: '',
        key2: filter.value2,
        value2: '',
        type: filter.subFilters.type,
        listOfValue: [],
        applyFilter: false,
        enable: true,
      };
    } else if (filter.subFilters.type === filterType.ANGLE_BRACKETS) {
      singleFilter = {
        name: filter.label,
        key: filter.subFilters.type,
        value: '',
        key1: `${filter.value}Value`,
        value1: '',
        key2: `${filter.value}Operator`, // Need to configure a common value in both FE and BE
        value2: '',
        type: filter.subFilters.type,
        listOfValue: Array.isArray(filter?.subFilters?.options)
          ? filter?.subFilters?.options?.map((option) =>
              typeof option === 'string' ? { label: option, value: option } : option,
            )
          : [],
        applyFilter: false,
        enable: true,
      };
    } else {
      singleFilter = {
        name: filter.label,
        key: filter.value,
        type: filter.subFilters.type,
        value:
          filter.subFilters.type === filterType.CHECKBOX ||
          filterType.MULTISELECT ||
          filterType.SELECT_FIELD
            ? []
            : '',
        listOfValue: Array.isArray(filter?.subFilters?.options)
               ? filter?.subFilters?.options
              .filter(
                (option) => option && (typeof option === 'string' || typeof option === 'object'),
              )
              .map((option) =>
                typeof option === 'string' ? { label: option, value: option } : option,
              )
          : [],
        applyFilter: false,
        enable: true,
      };
    }
    return singleFilter;
  });
  return updatedFilters;
}

/**
 * Function to update a value to filter
 * @param existingFilters
 * @param updatedValue
 */
function UpdateCommonFilter(
  existingFilters: CommonFilterProperties[],
  updatedValue: FilterUpdateProperties,
) {
  let isFilterUpdated = false;
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if (updatedValue.key === filterType.DATE_RANGE) {
      if (updatedValue.subKey === updatedFilter.key1) {
        updatedFilter = {
          ...updatedFilter,
          value1: updatedValue.value.toString(),
          applyFilter: true,
        };
        isFilterUpdated = true;
      } else if (updatedValue.subKey === updatedFilter.key2) {
        updatedFilter = {
          ...updatedFilter,
          value2: updatedValue.value.toString(),
          applyFilter: true,
        };
        isFilterUpdated = true;
      } else {
        console.error('SubKey not found', updatedValue.subKey);
      }
    } else if (updatedValue.key === filterType.ANGLE_BRACKETS) {
      if (updatedValue.subKey === updatedFilter.key1) {
        updatedFilter = {
          ...updatedFilter,
          value1: updatedValue.value.toString(),
          applyFilter: Boolean(updatedValue.value && updatedFilter.value2),
        };
        isFilterUpdated = Boolean(updatedValue.value && updatedFilter.value2);
      } else if (updatedValue.subKey === updatedFilter.key2) {
        updatedFilter = {
          ...updatedFilter,
          value2: updatedValue.value.toString(),
          applyFilter: Boolean(updatedValue.value && updatedFilter.value1),
        };
        isFilterUpdated = Boolean(updatedValue.value && updatedFilter.value1);
      } else {
        console.error('SubKey not found', updatedValue.subKey);
      }
    } else if (filter.key === updatedValue.key) {
      isFilterUpdated = true;
      switch (filter.type) {
        case filterType.CHECKBOX:
          if (Array.isArray(filter.value)) {
            const valueExists = filter.value.includes(updatedValue.value.toString());
            updatedFilter = {
              ...filter,
              value: valueExists
                ? filter.value.filter((value) => value !== updatedValue.value.toString())
                : [...filter.value, updatedValue.value.toString()],
            };
          }
          if (updatedFilter.value.length > 0) {
            updatedFilter.applyFilter = true;
          } else {
            updatedFilter.applyFilter = false;
          }
          break;
        case filterType.MULTISELECT:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue?.value,
            valueToShow: updatedValue?.valueToShow,
            applyFilter: updatedValue?.value?.length > 0,
          };
          break;
        default:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue.value,
            applyFilter: updatedValue.value !== '',
          };
          break;
      }
    }
    return updatedFilter;
  });

  return { updatedCommonFilter, isFilterUpdated };
}

/**
 * Function to update a value to filter
 * @param existingFilters
 * @param updatedValue
 * @returns
 */
function ReplaceValueInFilter(
  existingFilters: CommonFilterProperties[],
  updatedValue: FilterUpdateProperties,
) {
  let isFilterUpdated = false;
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if (filter.key === updatedValue.key) {
      isFilterUpdated = true;
      switch (filter.type) {
        case filterType.CHECKBOX:
          if (Array.isArray(filter.value)) {
            updatedFilter = {
              ...filter,
              value:
                typeof updatedValue.value === 'string' ? [updatedValue.value] : updatedValue.value,
            };
          }
          if (updatedFilter.value.length > 0) {
            updatedFilter.applyFilter = true;
          } else {
            updatedFilter.applyFilter = false;
          }
          break;
        case filterType.MULTISELECT:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue?.value,
            valueToShow: updatedValue?.valueToShow,
            applyFilter: updatedValue?.value?.length > 0,
          };
          break;
        default:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue.value,
            applyFilter: updatedValue.value !== '',
          };
          break;
      }
    }
    return updatedFilter;
  });
  return { updatedCommonFilter, isFilterUpdated };
}

/**
 * Function to generate params based on filters array of object
 * @param filters
 */
function GetParametersFromCommonFilters(filters: CommonFilterProperties[]) {
  const filterParameters: any = {};
  filters.forEach((filter: CommonFilterProperties) => {
    if (filter.applyFilter && filter.key === filterType.DATE_RANGE) {
      if (filter.value1 !== '') {
        filterParameters[filter.key1 || 'startDate'] = filter.value1;
      }
      if (filter.value2 !== '') {
        filterParameters[filter.key2 || 'endDate'] = filter.value2;
      }
    } else if (filter.applyFilter && filter.key === filterType.TIME_RANGE) {
      if (filter.value1 !== '') {
        filterParameters[filter.key1 || 'startTime'] = filter.value1;
      }
      if (filter.value2 !== '') {
        filterParameters[filter.key2 || 'endTime'] = filter.value2;
      }
    } else if (filter.applyFilter && filter.key === filterType.ANGLE_BRACKETS) {
      if (filter.value1 !== '') {
        filterParameters[filter.key1 || ''] = filter.value1;
      }
      if (filter.value2 !== '') {
        filterParameters[filter.key2 || ''] = filter.value2;
      }
    } else if (filter.applyFilter) {
      filterParameters[filter.key] = filter.value;
    }
  });
  return filterParameters;
}

/**
 * Function to remove a applied filter and return to initial state
 * @param existingFilters
 * @param key
 */
function RemoveSingleFilterFromList(existingFilters: CommonFilterProperties[], key: string) {
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if (filter.key === key) {
      switch (filter.type) {
        case filterType.CHECKBOX:
          updatedFilter = {
            ...filter,
            applyFilter: false,
            value: [],
          };
          break;
        case filterType.DATE_RANGE:
        case filterType.TIME_RANGE:
        case filterType.ANGLE_BRACKETS:
          updatedFilter = {
            ...filter,
            applyFilter: false,
            value1: '',
            value2: '',
          };
          break;
        default:
          updatedFilter = {
            ...updatedFilter,
            value: '',
            applyFilter: false,
          };
          break;
      }
    }
    return updatedFilter;
  });

  return updatedCommonFilter;
}

/**
 * Function to remove a applied filter and return to initial state
 * @param existingFilters
 * @param key
 */
function ClearFilters(existingFilters: CommonFilterProperties[]) {
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if(filter.disableClearFilter) {
        return filter; // Skip filters that should not be cleared
    }
    switch (filter.type) {
      case filterType.CHECKBOX:
        updatedFilter = {
          ...filter,
          applyFilter: false,
          value: [],
        };
        break;
      case filterType.DATE_RANGE:
      case filterType.TIME_RANGE:
      case filterType.ANGLE_BRACKETS:
        updatedFilter = {
          ...filter,
          applyFilter: false,
          value1: '',
          value2: '',
        };
        break;
      default:
        updatedFilter = {
          ...updatedFilter,
          value: '',
          applyFilter: false,
        };
        break;
    }
    return updatedFilter;
  });

  return updatedCommonFilter;
}
/**
 * Function that will reset all filters and add a single filter
 * @param existingFilters
 * @param updatedValue
 * @returns
 */
const RestFilterAndAddSingleFilter = (
  existingFilters: CommonFilterProperties[],
  updatedValue: FilterUpdateProperties,
) => {
  const unappliedFilterList: CommonFilterProperties[] = ClearFilters(existingFilters);
  const updatedCommonFilter: CommonFilterProperties[] =
    ReplaceValueInFilter(unappliedFilterList, updatedValue)?.updatedCommonFilter || [];

  return { updatedCommonFilter, isFilterUpdated: true };
};

export {
  UpdateCommonFilter,
  ConfigureListFilterResponse,
  GetParametersFromCommonFilters,
  RemoveSingleFilterFromList,
  ClearFilters,
  ReplaceValueInFilter,
  RestFilterAndAddSingleFilter
};
